// Port of webapp/src/components/cardDetail/cardDetail.tsx (+ cardDialog.tsx,
// which is only the full-height overlay wrapper around it).

import {ReactElement, useEffect, useMemo} from 'react'
import {Board} from '../../types/board'
import {BoardView} from '../../types/boardView'
import {Card} from '../../types/card'
import {useContents} from '../../store/contents'
import {useComments, selectCommentsOfCard} from '../../store/comments'
import * as mutator from '../../state/mutator'
import {Icon} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem, MenuSeparator} from '../common/menu'
import Editable from '../common/editable'
import CardDetailProperties from './cardDetailProperties'
import CardDetailContents from './cardDetailContents'
import CommentsList from './commentsList'

type Props = {
    board: Board
    view: BoardView
    cards: Card[]
    cardId: string
    readOnly: boolean
    showCard: (cardId: string | undefined) => void
}

export default function CardDetail({board, view, cards, cardId, readOnly, showCard}: Props): ReactElement | null {
    const card = cards.find((c) => c.id === cardId)
    const contents = useContents((state) => state.contents)
    const loadedFor = useContents((state) => Boolean(state.loadedForCardIds[cardId]))
    const loadBlocksOfCard = useContents((state) => state.loadBlocksOfCard)
    const comments = useComments((state) => selectCommentsOfCard(state, cardId))

    useEffect(() => {
        if (!loadedFor) {
            void loadBlocksOfCard(cardId)
        }
    }, [cardId, loadedFor, loadBlocksOfCard])

    // Esc closes, like upstream's card dialog
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                showCard(undefined)
            }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [showCard])

    const blocks = useMemo(
        () => card ? card.fields.contentOrder.map((entry) => {
            const id = typeof entry === 'string' ? entry : entry[0]
            return contents[id]
        }).filter(Boolean) : [],
        [card, contents],
    )

    const ordered = useMemo(() => {
        const byOrder = view.fields.cardOrder.filter((id) => cards.some((c) => c.id === id))
        const rest = cards.filter((c) => !byOrder.includes(c.id)).map((c) => c.id)
        return [...byOrder, ...rest]
    }, [cards, view.fields.cardOrder])

    const index = ordered.indexOf(cardId)
    const prevId = index > 0 ? ordered[index - 1] : undefined
    const nextId = index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : undefined

    if (!card) {
        return null
    }

    return (
        <div className='CardDetail flex h-full w-full flex-col overflow-hidden' data-card-id={card.id}>
            <header className='flex shrink-0 items-center gap-2 border-b border-main-fg/10 px-6 py-3'>
                <ButtonWithMenu
                    button={
                        <span className='IconButton' aria-label='卡片操作'>
                            <Icon name='dots-vertical' size={18}/>
                        </span>
                    }
                >
                    <Menu ariaLabel='卡片操作' align='left'>
                        <MenuItem
                            icon='duplicate'
                            disabled={readOnly}
                            onClick={() => void mutator.createCard(board, view, {
                                title: `${card.title}（副本）`,
                                icon: card.fields.icon,
                                properties: card.fields.properties,
                            })}
                        >
                            创建副本
                        </MenuItem>
                        <MenuItem icon='link' onClick={() => void navigator.clipboard?.writeText(window.location.href)}>
                            复制链接
                        </MenuItem>
                        <MenuSeparator/>
                        <MenuItem
                            icon='trash'
                            danger={true}
                            disabled={readOnly}
                            onClick={() => {
                                void mutator.archiveCard(board.id, card)
                                showCard(undefined)
                            }}
                        >
                            删除卡片
                        </MenuItem>
                    </Menu>
                </ButtonWithMenu>

                <div className='ml-auto flex items-center gap-1'>
                    <button
                        type='button'
                        className='IconButton'
                        disabled={!prevId}
                        onClick={() => prevId && showCard(prevId)}
                        aria-label='上一张卡片'
                    >
                        <Icon name='chevron-up' size={16}/>
                    </button>
                    <span className='text-xs tabular-nums opacity-60'>
                        {index >= 0 ? index + 1 : '-'} / {ordered.length}
                    </span>
                    <button
                        type='button'
                        className='IconButton'
                        disabled={!nextId}
                        onClick={() => nextId && showCard(nextId)}
                        aria-label='下一张卡片'
                    >
                        <Icon name='chevron-down' size={16}/>
                    </button>
                    <button type='button' className='IconButton' onClick={() => showCard(undefined)} aria-label='关闭'>
                        <Icon name='close' size={16}/>
                    </button>
                </div>
            </header>

            <div className='flex-1 overflow-auto px-6 py-5 lg:px-24'>
                <div className='mx-auto max-w-[760px]'>
                    <div className='mb-4 flex items-start gap-3'>
                        {!readOnly && (
                            <Editable
                                className='w-9 shrink-0 text-center text-2xl'
                                value={card.fields.icon || ''}
                                placeholder='🙂'
                                allowEmpty={true}
                                onChange={(icon) => void mutator.changeCardIcon(board.id, card, icon)}
                            />
                        )}
                        {readOnly && card.fields.icon && <span className='text-2xl'>{card.fields.icon}</span>}
                        <Editable
                            className='flex-1 text-2xl font-bold'
                            value={card.title}
                            placeholder='未命名'
                            readOnly={readOnly}
                            allowEmpty={false}
                            onChange={(title) => void mutator.changeCardTitle(board.id, card, title)}
                        />
                    </div>

                    <CardDetailProperties board={board} card={card} readOnly={readOnly}/>

                    <div className='my-6 h-px bg-main-fg/10'/>

                    <CardDetailContents board={board} card={card} blocks={blocks} readOnly={readOnly}/>

                    <CommentsList board={board} card={card} comments={comments} readOnly={readOnly}/>
                </div>
            </div>
        </div>
    )
}
